import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { X, DiceFive, PencilSimple, ArrowRight } from '@phosphor-icons/react'
import type { Choice, WealthConfig } from '../types'
import { runSimulation } from '../api'
import { useStore } from '../store'
import { analytics } from '../lib/analytics'
import { toast } from './Toast'
import { useDialog } from '../hooks/useDialog'

/** A fully-populated decision draft (template output) the modal can seed from. */
export interface WarmStartSeed {
  name: string
  horizon_years: number
  discount_rate: number
  risk_aversion: number
  choices: Choice[]
  wealth?: WealthConfig
  base_currency?: string
}

interface Props {
  seed: WarmStartSeed | null
  /** Where the modal was opened from — analytics only. */
  source: string
  onClose: () => void
}

interface KeyInput {
  choiceId: string
  choiceName: string
  varId: string
  varName: string
  units?: string
  defaultMu: number
}

/** The one number per option that matters most: the biggest income driver,
 *  falling back to the biggest numeric driver of any role. */
function deriveKeyInputs(choices: Choice[]): KeyInput[] {
  const inputs: KeyInput[] = []
  for (const c of choices.slice(0, 3)) {
    let best = null as null | { varId: string; varName: string; units?: string; mu: number; income: boolean }
    for (const v of c.variables) {
      const mu = v.dist?.params?.mu
      if (typeof mu !== 'number' || mu === 0 || v.dist?.kind === 'Bernoulli') continue
      const income = v.role === 'income'
      if (
        !best ||
        (income && !best.income) ||
        (income === best.income && Math.abs(mu) > Math.abs(best.mu))
      ) {
        best = { varId: v.id, varName: v.name, units: v.units, mu, income }
      }
    }
    if (best) {
      inputs.push({
        choiceId: c.id,
        choiceName: c.name,
        varId: best.varId,
        varName: best.varName,
        units: best.units,
        defaultMu: best.mu,
      })
    }
  }
  return inputs
}

/** Replace each edited variable's mu, ratio-scaling sigma so the relative
 *  uncertainty the template encodes survives the user's number. */
function applyInputs(choices: Choice[], inputs: KeyInput[], values: Record<string, number>): Choice[] {
  return choices.map((c) => ({
    ...c,
    variables: c.variables.map((v) => {
      const input = inputs.find((k) => k.choiceId === c.id && k.varId === v.id)
      if (!input || !v.dist) return v
      const to = values[`${c.id}::${v.id}`]
      if (typeof to !== 'number' || !isFinite(to) || to === input.defaultMu) return v
      const params: Record<string, number> = { ...v.dist.params, mu: to }
      if (typeof params.sigma === 'number' && input.defaultMu !== 0) {
        params.sigma = Math.abs(params.sigma * (to / input.defaultMu))
      }
      return { ...v, dist: { ...v.dist, params } }
    }),
  }))
}

/**
 * Two-question warm start: the user corrects a populated model instead of
 * filling an empty one. Confirm the one number that matters per option →
 * we seed the full template, run 1,000 futures, and land on Results with
 * the verdict live. "Customize first" keeps the classic builder path.
 */
export default function WarmStartModal({ seed, source, onClose }: Props) {
  const navigate = useNavigate()
  const { addDecision, saveResult } = useStore()
  const [values, setValues] = useState<Record<string, string>>({})
  const [busy, setBusy] = useState(false)

  const inputs = useMemo(() => (seed ? deriveKeyInputs(seed.choices) : []), [seed])
  const dialogRef = useDialog<HTMLDivElement>(!!seed, () => { if (!busy) onClose() })

  if (!seed) return null

  const numericValues = (): Record<string, number> => {
    const out: Record<string, number> = {}
    for (const k of inputs) {
      const key = `${k.choiceId}::${k.varId}`
      const raw = values[key]
      const n = raw === undefined || raw.trim() === '' ? k.defaultMu : Number(raw.replace(/[,\s]/g, ''))
      out[key] = isFinite(n) ? n : k.defaultMu
    }
    return out
  }

  const seedDecision = (): string =>
    addDecision({
      name: seed.name,
      horizon_years: seed.horizon_years,
      discount_rate: seed.discount_rate,
      risk_aversion: seed.risk_aversion,
      base_currency: seed.base_currency,
      wealth: seed.wealth,
      choices: applyInputs(seed.choices, inputs, numericValues()),
    } as any)

  const runNow = async () => {
    if (busy) return
    setBusy(true)
    const id = seedDecision()
    const decision = useStore.getState().decisions.find((d) => d.id === id)!
    try {
      const result = await runSimulation({
        decision_id: id,
        decision_name: decision.name,
        horizon_years: decision.horizon_years,
        discount_rate: decision.discount_rate,
        risk_aversion: decision.risk_aversion,
        wellbeing_value: decision.wellbeing_value,
        n_simulations: 1000,
        wealth: decision.wealth?.enabled ? decision.wealth : undefined,
        choices: decision.choices,
      })
      saveResult(id, result)
      analytics.track('warm_start_completed', { source, n_inputs: inputs.length })
      onClose()
      navigate(`/results/${id}`)
    } catch (e: any) {
      // The decision is seeded either way — degrade to the builder, where the
      // user can retry the run with full error context.
      setBusy(false)
      toast({ kind: 'warning', title: 'Simulation failed', message: 'Opening the builder instead — hit Run there.' })
      onClose()
      navigate(`/decision/${id}`)
    }
  }

  const customizeFirst = () => {
    const id = seedDecision()
    analytics.track('warm_start_customize', { source })
    onClose()
    navigate(`/decision/${id}`)
  }

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-4" data-testid="warm-start-modal">
      <div className="absolute inset-0 bg-ink/40 backdrop-blur-sm" onClick={busy ? undefined : onClose} />
      <div ref={dialogRef} role="dialog" aria-modal="true" aria-labelledby="warm-start-title" tabIndex={-1} className="outline-none anim-pop relative bg-bg border-2 border-edge rounded-2xl shadow-brutal-lg w-full max-w-md p-6">
        <div className="flex items-start justify-between mb-1">
          <div className="flex items-center gap-2">
            <div className="w-10 h-10 rounded-xl bg-primary text-white grid place-items-center border-2 border-edge shadow-brutal-sm">
              <DiceFive size={20} weight="fill" />
            </div>
            <div>
              <div className="label-bold">Quick start</div>
              <h3 id="warm-start-title" className="font-heading text-xl font-bold leading-tight">{seed.name}</h3>
            </div>
          </div>
          <button onClick={onClose} disabled={busy} className="p-2 hover:bg-muted rounded-lg" aria-label="Close" data-testid="warm-start-close">
            <X size={20} weight="bold" />
          </button>
        </div>

        <p className="text-sm text-ink-muted mb-4">
          We've pre-filled realistic numbers for everything. Confirm the {inputs.length === 1 ? 'one' : inputs.length}{' '}
          that matter{inputs.length === 1 ? 's' : ''} most and we'll simulate 1,000 futures right away —
          you can refine every other assumption afterwards.
        </p>

        <div className="space-y-3 mb-5">
          {inputs.map((k) => {
            const key = `${k.choiceId}::${k.varId}`
            return (
              <div key={key}>
                <label htmlFor={`warm-start-input-${k.choiceId}`} className="label-bold mb-1 block text-sm">
                  {k.varName} — <span className="text-ink-muted font-medium">{k.choiceName}</span>
                  {k.units && <span className="text-xs text-ink-muted font-medium ml-1">({k.units})</span>}
                </label>
                <input
                  id={`warm-start-input-${k.choiceId}`} data-testid={`warm-start-input-${k.choiceId}`}
                  className="input-brutal w-full mono"
                  inputMode="decimal"
                  placeholder={k.defaultMu.toLocaleString()}
                  value={values[key] ?? ''}
                  onChange={(e) => setValues((v) => ({ ...v, [key]: e.target.value }))}
                  onKeyDown={(e) => e.key === 'Enter' && runNow()}
                />
              </div>
            )
          })}
        </div>

        <div className="flex flex-col sm:flex-row gap-2">
          <button
            onClick={runNow}
            disabled={busy}
            className="btn-primary flex-1 inline-flex items-center justify-center gap-1.5 disabled:opacity-60"
            data-testid="warm-start-run-btn"
          >
            {busy ? 'Sampling 1,000 futures…' : <>Run 1,000 futures <ArrowRight size={16} weight="bold" /></>}
          </button>
          <button
            onClick={customizeFirst}
            disabled={busy}
            className="btn-secondary inline-flex items-center justify-center gap-1.5 disabled:opacity-60"
            data-testid="warm-start-customize-btn"
          >
            <PencilSimple size={16} weight="bold" /> Customize first
          </button>
        </div>
      </div>
    </div>
  )
}
